import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  DebouncedSearchInput,
  ModalOpenButton,
  useScreenWidth,
} from "@msys/ui";
import { AddShoppingCart as AddShoppingCartIcon } from "@mui/icons-material";
import {
  Button,
  Divider,
  IconButton,
  Stack,
  SvgIconProps,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormikContext } from "formik";
import React from "react";
import { v4 as uuid } from "uuid";
import { BackButton } from "../../commons/button/BackButton.js";
import { ButtonCircleWrapper } from "../../commons/button/ButtonCircleWrapper.js";
import { AddProductListConfirmModal } from "./AddProductListConfirmModal.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { usePaginationParamsInUrl } from "../../commons/pagination/usePaginationParams.js";
import { FormValues } from "../doc-items/SearchProductsAndTemplates.js";
import { ProductsListView } from "../products/components/ProductsListView.js";
import { ProductOverviewModal } from "../products/modals/ProductOverviewModal.js";
import { FilterByProductListAuthorsButton } from "./FilterByProductListAuthorsButton.js";
import { useDataGridStateStore } from "../users/useDataGridStateStore.js";
import {
  ProductListsQueryVariables,
  useProductListsQuery,
} from "../../main-routes/products/ProductLists.generated.js";
import {
  ProductListsFilters,
  useProductListsList,
} from "./useProductListsList.js";
import { ProductListsCollectionView } from "./ProductListsCollectionView.js";
import { ProductListsCollectionView_ProductListFragment } from "./ProductListsCollectionView.generated.js";
import {
  ProductListItems_ProductFragment,
  useProductListItemsLazyQuery,
  useProductListItemsQuery,
} from "./ProductListItems.generated.js";

const ALLOWED_VIEWS: CollectionView[] = ["gallery", "list", "table"];

/** A product list, minimally, as needed to drill into it. */
type SelectedProductList = { id: string; title: string };

/** A drill-in row: the resolved product plus the stored item id + quantity. */
type ProductListItemRow = ProductListItems_ProductFragment & {
  productListItemId: string;
  itemQuantity: number;
};

interface Props {
  initialSearchValue?: string;
  handleSearchValueChange?(value: string): void;
  Form: React.ReactNode;
  AddToListIcon?: React.ComponentType<SvgIconProps>;
  // When set, the component opens directly on this list's products (drill-in).
  initialProductListId?: string;
  initialProductListTitle?: string;
  tabs?: React.ReactNode;
}

/**
 * Explore product lists and add their products to the selection cart —
 * modelled on `ProductSelectMultipleComponent`. Two levels: a list-of-lists
 * (search + `ProductListsCollectionView`) and, once a list is picked, its
 * products (`ProductsListView`) with per-product add + stored quantity.
 */
export const ProductListSelectMultipleComponent = ({
  initialSearchValue = "",
  handleSearchValueChange,
  Form,
  AddToListIcon = AddShoppingCartIcon,
  initialProductListId,
  initialProductListTitle,
  tabs,
}: Props) => {
  const { isMinTablet } = useScreenWidth();

  const [selectedList, setSelectedList] =
    React.useState<SelectedProductList | null>(
      initialProductListId
        ? { id: initialProductListId, title: initialProductListTitle ?? "" }
        : null
    );

  return (
    <Stack
      direction={isMinTablet ? "row" : "column"}
      spacing={{ xs: 1, md: 1.5 }}
      sx={{ height: "100%", alignItems: "stretch" }}
    >
      <Stack
        direction="column"
        spacing={1}
        sx={{ height: "100%", flex: 1, minWidth: 0 }}
      >
        {tabs}
        {selectedList ? (
          <ProductListProductsSelect
            productList={selectedList}
            onBack={() => setSelectedList(null)}
            AddToListIcon={AddToListIcon}
          />
        ) : (
          <ProductListsExplore
            initialSearchValue={initialSearchValue}
            handleSearchValueChange={handleSearchValueChange}
            onChooseProducts={setSelectedList}
          />
        )}
      </Stack>
      {isMinTablet && (
        <>
          <Divider orientation="vertical" flexItem />
          <Stack
            direction="column"
            spacing={1}
            sx={{
              height: "100%",
              overflow: "hidden",
              flexGrow: 0,
              flexShrink: 0,
              width: theme => theme.layout.columnWidth.md,
            }}
          >
            {Form}
          </Stack>
        </>
      )}
    </Stack>
  );
};

/** Level 1 — browse the org's product lists. */
function ProductListsExplore({
  initialSearchValue,
  handleSearchValueChange,
  onChooseProducts,
}: {
  initialSearchValue: string;
  handleSearchValueChange?(value: string): void;
  onChooseProducts: (productList: SelectedProductList) => void;
}) {
  const { t } = useTranslate(["ProductLists", "Global"]);
  const client = useApolloClient();

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    sorting,
    setSorting,
    activeView,
    setActiveView,
    queryBaseVariables,
  } = useProductListsList<
    ProductListsFilters & Pick<ProductListsQueryVariables, "offset" | "limit">
  >({
    activeViewStorageKey: "product-list-select",
    activeViewInitialDesktopValue: "list",
    activeViewInitialMobileValue: "list",
    initialFilters: { searchTerm: initialSearchValue },
  });

  const withImages = activeView !== "table";

  const query = useProductListsQuery({
    client,
    variables: {
      ...queryBaseVariables,
      sorting,
      searchTerm: filters.searchTerm,
      filters: {
        authorIds: filters.authorIds,
      },
      withImages,
    },
  });

  const productLists = getDataOrNull(
    (query.data ?? query.previousData)?.productLists
  );
  const items = productLists?.edges.map(e => e.node) ?? [];
  const totalCount = productLists?.totalCount ?? 0;

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <DebouncedSearchInput
          placeholder={t("Search", { ns: "Global" })}
          defaultValue={filters.searchTerm ?? ""}
          onChangeSearchTerm={newValue => {
            handleSearchValueChange?.(newValue ?? "");
            setFilters(f => ({ ...f, searchTerm: newValue }));
          }}
          sx={{ flex: 1 }}
          autoFocus
        />
        <FilterByProductListAuthorsButton
          inputLabel={t("Created by", { ns: "Global" })}
          placeholder={t("Search by author", { ns: "ProductLists" })}
          value={filters.authorIds ?? []}
          setValue={newValue =>
            setFilters(f => ({ ...f, authorIds: newValue }))
          }
        />
        <SwitchCollectionViewButton
          allowedViews={ALLOWED_VIEWS}
          activeView={activeView}
          setActiveView={setActiveView}
        />
      </Stack>
      <ProductListsCollectionView
        items={items}
        totalCount={totalCount}
        activeView={activeView}
        loading={query.loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sorting={sorting}
        setSorting={setSorting}
        onProductListClick={productList =>
          onChooseProducts({ id: productList.id, title: productList.title })
        }
        RowActions={({ productList }) => (
          <AddProductListButton
            productList={productList}
            onChooseProducts={() =>
              onChooseProducts({ id: productList.id, title: productList.title })
            }
          />
        )}
      />
    </>
  );
}

/** The per-list "add" action: opens the "Add product list" confirm modal. */
function AddProductListButton({
  productList,
  onChooseProducts,
}: {
  productList: ProductListsCollectionView_ProductListFragment;
  onChooseProducts: () => void;
}) {
  const { t } = useTranslate(["ProductLists"]);
  const client = useApolloClient();
  const formikProps = useFormikContext<FormValues>();

  // Load the list's items only when confirming "add all".
  const [fetchItems] = useProductListItemsLazyQuery({ client });

  return (
    <ButtonCircleWrapper>
      <ModalOpenButton
        Modal={AddProductListConfirmModal}
        modalProps={{
          title: productList.title,
          itemCount: productList.itemCount,
          onConfirm: async () => {
            const { data } = await fetchItems({
              variables: { productListId: productList.id },
            });
            const items =
              getDataOrNull(data?.productListItems)?.edges.map(e => e.node) ??
              [];
            addProductListItemsToCart(formikProps, items);
          },
          onChooseProducts,
        }}
      >
        <IconButton
          size="small"
          color="primary"
          aria-label={t("Add all products", { ns: "ProductLists" })}
        >
          <AddShoppingCartIcon fontSize="small" />
        </IconButton>
      </ModalOpenButton>
    </ButtonCircleWrapper>
  );
}

/** Level 2 — a single list's products, addable individually. */
function ProductListProductsSelect({
  productList,
  onBack,
  AddToListIcon,
}: {
  productList: SelectedProductList;
  onBack: () => void;
  AddToListIcon: React.ComponentType<SvgIconProps>;
}) {
  const { t } = useTranslate(["ProductLists", "ProductSearch"]);
  const client = useApolloClient();
  const formikProps = useFormikContext<FormValues>();
  const stateStore = useDataGridStateStore(
    "ProductListSelectMultiple-Products"
  );
  const [overviewProduct, setOverviewProduct] =
    React.useState<ProductListItemRow | null>(null);
  // Own URL params: the list browser one level up uses the defaults, so
  // sharing them would carry its page number into the drilled-in products
  // (and vice versa on the way back).
  const { paginationModel, setPaginationModel } = usePaginationParamsInUrl({
    pageUrlParam: "productsPage",
    perPageUrlParam: "productsPageSize",
  });
  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "product-list-select-products",
      "list",
      "list"
    );

  const query = useProductListItemsQuery({
    client,
    variables: { productListId: productList.id },
  });
  const items =
    getDataOrNull(
      (query.data ?? query.previousData)?.productListItems
    )?.edges.map(e => e.node) ?? [];

  // Each row is the resolved product plus the stored item id + quantity.
  const rows = React.useMemo<ProductListItemRow[]>(
    () =>
      items.flatMap(item =>
        item.product
          ? [
              {
                ...item.product,
                productListItemId: item.id,
                itemQuantity: item.quantity,
              },
            ]
          : []
      ),
    [items]
  );

  // The gallery/list branches of `ProductsListView` render every row they are
  // given, so they have to be handed the current page; the table paginates the
  // full array itself.
  const visibleRows = React.useMemo(
    () =>
      activeView === "table"
        ? rows
        : rows.slice(
            paginationModel.page * paginationModel.pageSize,
            (paginationModel.page + 1) * paginationModel.pageSize
          ),
    [rows, activeView, paginationModel.page, paginationModel.pageSize]
  );

  const CardActionButton = React.useCallback(
    ({ product }: { product: (typeof rows)[number] }) => (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <ProductQuantity quantity={product.itemQuantity} />
        <ButtonCircleWrapper>
          <AddProductToCartButton
            product={product}
            AddToListIcon={AddToListIcon}
          />
        </ButtonCircleWrapper>
      </Stack>
    ),
    [AddToListIcon]
  );
  const TableActions = React.useCallback(
    ({ product }: { product: (typeof rows)[number] }) => (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <ProductQuantity quantity={product.itemQuantity} />
        <AddProductToCartButton
          product={product}
          AddToListIcon={AddToListIcon}
        />
      </Stack>
    ),
    [AddToListIcon]
  );

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <BackButton onClick={onBack} />
        <Typography variant="h3" sx={{ flex: 1 }} noWrap>
          {productList.title}
        </Typography>
        <Button
          size="small"
          variant="text"
          color="primary"
          startIcon={<AddToListIcon />}
          disabled={rows.length === 0}
          onClick={() => addProductListItemsToCart(formikProps, items)}
        >
          {t("Add all products", { ns: "ProductLists" })}
        </Button>
        <SwitchCollectionViewButton
          allowedViews={ALLOWED_VIEWS}
          activeView={activeView}
          setActiveView={setActiveView}
        />
      </Stack>
      <ProductsListView
        items={visibleRows}
        totalCount={rows.length}
        activeView={activeView}
        loading={query.loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        getRowId={row => row.productListItemId}
        onClick={setOverviewProduct}
        CardActionButton={CardActionButton}
        TableActions={TableActions}
        stateStore={stateStore}
        tableActionsWidth={80}
      />
      {overviewProduct && (
        <ProductOverviewModal
          productArticleNumber={overviewProduct.articleNumber}
          productSupplierId={overviewProduct.supplierId}
          handleClose={() => setOverviewProduct(null)}
          actionButtonProps={{
            label: t("Add to list", { ns: "ProductSearch" }),
            handleClick: () => {
              const { itemQuantity, productListItemId, ...cartProduct } =
                overviewProduct;
              formikProps.setFieldValue("products", [
                ...formikProps.values.products,
                {
                  quantity: itemQuantity > 0 ? itemQuantity : 1,
                  product: cartProduct,
                  selectionId: uuid(),
                },
              ]);
              setOverviewProduct(null);
            },
            buttonProps: {
              color: "primary" as const,
              variant: "contained" as const,
              startIcon: <AddToListIcon />,
            },
          }}
        />
      )}
    </>
  );
}

/** The stored quantity shown next to a product's add button, e.g. "×3". */
function ProductQuantity({ quantity }: { quantity: number }) {
  return (
    <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
      ×{quantity}
    </Typography>
  );
}

/** Adds a single product-list product (with its stored quantity) to the cart. */
function AddProductToCartButton({
  product,
  AddToListIcon,
}: {
  product: ProductListItems_ProductFragment & { itemQuantity: number };
  AddToListIcon: React.ComponentType<SvgIconProps>;
}) {
  const { t } = useTranslate(["ProductSearch"]);
  const formikProps = useFormikContext<FormValues>();
  const { itemQuantity, ...cartProduct } = product;
  return (
    <IconButton
      size="small"
      color="primary"
      aria-label={t("Add to list", { ns: "ProductSearch" })}
      onClick={e => {
        e.stopPropagation();
        formikProps.setFieldValue("products", [
          ...formikProps.values.products,
          {
            quantity: itemQuantity > 0 ? itemQuantity : 1,
            product: cartProduct,
            selectionId: uuid(),
          },
        ]);
      }}
    >
      <AddToListIcon fontSize="small" />
    </IconButton>
  );
}

function addProductListItemsToCart(
  formikProps: ReturnType<typeof useFormikContext<FormValues>>,
  items: {
    quantity: number;
    product?: ProductListItems_ProductFragment | null;
  }[]
) {
  // Always append a fresh entry per item (never merge into an existing one),
  // so adding a list — or the same product again — yields distinct rows.
  const additions = items.flatMap(item =>
    item.product
      ? [
          {
            quantity: item.quantity > 0 ? item.quantity : 1,
            product: item.product,
            selectionId: uuid(),
          },
        ]
      : []
  );
  formikProps.setFieldValue("products", [
    ...formikProps.values.products,
    ...additions,
  ]);
}
